import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Avatar,
  Rating,
  TextField,
  InputAdornment,
  Skeleton,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../state/store';
import { getProductFeed, searchProducts, selectFeed, selectSearchResults, selectIsSearching } from '../state/slices/products';
import { selectIdToken } from '../state/slices/user';
import Paths from '../paths';



const CourseFeed: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // Add CSS custom properties for theme switching
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--theme-subtle-accent', 'rgba(0,0,0,0.02)');
    root.style.setProperty('--theme-background-pattern', 'rgba(0,0,0,0.01)');
    root.style.setProperty('--theme-card-border', 'rgba(0,0,0,0.08)');
    root.style.setProperty('--theme-card-background', '#ffffff');
    root.style.setProperty('--theme-card-shadow', '0 1px 3px rgba(0,0,0,0.05)');
    root.style.setProperty('--theme-card-border-hover', 'rgba(0,0,0,0.15)');
    root.style.setProperty('--theme-card-shadow-hover', '0 4px 12px rgba(0,0,0,0.1)');
  }, []);

  const idToken = useAppSelector(selectIdToken);
  const feed = useAppSelector(selectFeed);
  const searchResults = useAppSelector(selectSearchResults);
  const isSearching = useAppSelector(selectIsSearching);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (idToken) {
      dispatch(getProductFeed(idToken));
    }
  }, [dispatch, idToken]);

  // Handle search
  const handleSearch = (query: string) => {
    if (query.trim().length > 0) {
      dispatch(searchProducts({ query: query.trim() }));
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch(searchQuery);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);



  const handleCourseClick = (productId: string) => {
    const productDetailsPath = Paths.PRODUCT_DETAILS.replace(':productId', productId);
    navigate(productDetailsPath);
  };

  const getPlaceholderImage = (courseTitle: string, courseId: string) => {
    // Generate a consistent color based on course title or ID
    const hash = (courseTitle + courseId).split('').reduce((a, b) => {
      a = ((a << 5) - a + b.charCodeAt(0)) & 0xffffffff;
      return a;
    }, 0);

    // Generate consistent colors based on hash
    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (Math.abs(hash) % 20); // 60-80%
    const lightness = 45 + (Math.abs(hash) % 15); // 45-60%

    // Create SVG placeholder with course initials
    const initials = courseTitle
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 3);

    const svg = `
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="225" fill="hsl(${hue}, ${saturation}%, ${lightness}%)"/>
        <text x="200" y="120" font-family="Arial, sans-serif" font-size="48" font-weight="bold"
              text-anchor="middle" fill="white" opacity="0.9">${initials}</text>
        <text x="200" y="150" font-family="Arial, sans-serif" font-size="16"
              text-anchor="middle" fill="white" opacity="0.7">Course</text>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

    const getCourseStats = (course: any) => {
    // Calculate actual course statistics from real data
    const lessonCount = course.content?.length || 0;

    return {
      lessonCount: lessonCount > 0 ? lessonCount : 0,
      // Duration removed - not a key selling point
      // Community size will be added in the future
    };
  };

      // Use search results if available, otherwise use feed data
  const coursesToDisplay = searchResults?.items || feed?.newReleases || [];
  
  let filteredCourses;
  if (searchQuery.trim().length > 0) {
    filteredCourses = coursesToDisplay;
  } else {
    filteredCourses = feed?.newReleases || [];
  }

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    // Handle different types - ProductDatum vs ProductDetailItem
    const aDate = (a as any).createdAt || (a as any).publishedAt || new Date(0);
    const bDate = (b as any).createdAt || (b as any).publishedAt || new Date(0);
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  // Add proper loading and error states
  const isLoading = !feed && !isSearching;
  const hasError = false; // TODO: Add error handling from Redux state
  const courseCount = searchResults?.pagination?.total || feed?.newReleases?.length || 0;



  if (!feed) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Box
                  sx={{
                    height: 200,
                    backgroundColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h4" color="grey.400">
                    Loading...
                  </Typography>
                </Box>
                <CardContent>
                  <Skeleton variant="text" height={24} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={16} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Learn from Peers
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Discover courses created by people like you
        </Typography>

        {/* Search Bar */}
        <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search for courses, skills, or instructors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                boxShadow: 2,
              },
            }}
          />
          {isSearching && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Searching...
            </Typography>
          )}
          {searchResults && searchQuery.trim().length > 0 && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: 1, textAlign: 'center' }}
            >
              Found {searchResults.searchInfo.resultsCount} results for "{searchQuery}"
            </Typography>
          )}
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={4} justifyContent="center">
          <Grid item>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              {filteredCourses.length}+
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Courses Available
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              {courseCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Community Creators
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              Always
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Available
            </Typography>
          </Grid>
        </Grid>
      </Box>



      {/* Course Count */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredCourses.length} of {courseCount} courses found
        </Typography>
      </Box>

      {/* Course Grid */}
      <Box
        sx={{
          background: `
            radial-gradient(circle at 20% 80%, rgba(0,0,0,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0,0,0,0.02) 0%, transparent 50%),
            linear-gradient(135deg, rgba(0,0,0,0.01) 0%, transparent 100%)
          `,
          borderRadius: 2,
          p: 4,
          mb: 4,
        }}
      >
        <Grid container spacing={6}>
          {sortedCourses.map((course, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id || index}>
                          <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                borderRadius: 0,
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.08)',
                backgroundColor: 'background.paper',
                padding: 3,
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                position: 'relative',
                overflow: 'visible',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                '&:hover': {
                  borderColor: 'rgba(0,0,0,0.15)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '& .course-image': {
                    transform: 'scale(1.02)',
                  },
                  '& .course-title': {
                    color: 'text.primary',
                  },
                  '& .enroll-button': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
              onClick={() => handleCourseClick(course.id || '')}
            >
              {/* Course Image Container */}
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 0,
                  height: 280,
                  marginBottom: 2,
                  marginLeft: -3,
                  marginRight: -3,
                  marginTop: -3,
                  '& .MuiCardMedia-root': {
                    height: 280,
                    minHeight: 280,
                    maxHeight: 280,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="280"
                  image={getPlaceholderImage(course.title, course.id || '')}
                  alt={course.title}
                  className="course-image"
                  sx={{
                    objectFit: 'cover',
                    transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  }}
                />
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 0, pt: 0 }}>
                {/* Course Title */}
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  className="course-title"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.3,
                    fontSize: '1.125rem',
                    color: 'text.primary',
                    mb: 1.5,
                    letterSpacing: '0.01em',
                    textTransform: 'none',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  {course.title}
                </Typography>

                {/* Creator Info */}
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  {course.creator?.displayName || 'Community Creator'}
                </Typography>

                {/* Student Enrollment Count - Future feature */}
                {/* TODO: Add studentCount property to ProductDetailItem when backend supports it */}
                {/* <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  py: 2,
                  backgroundColor: 'primary.50',
                  borderTop: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'primary.200',
                  borderRadius: 1,
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'primary.700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    👥 {course.studentCount || 0} students enrolled
                  </Typography>
                </Box> */}

                {/* Membership Access - Only show if user has access */}
                {/* TODO: Add proper access control properties to ProductDetailItem when backend supports it */}
                {/* For now, this is commented out until we have proper enrollment/subscription data */}
                {/*
                {(course.isPurchased || course.hasSubscriptionAccess || course.isOwner) ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'success.700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      borderTop: '1px solid',
                      borderColor: 'success.200',
                      pt: 2,
                      display: 'block',
                      backgroundColor: 'success.50',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                    }}
                  >
                    ✓ Included in Membership
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 2,
                      display: 'block',
                    }}
                  >
                    Join Membership to Access
                  </Typography>
                )}
                */}
              </CardContent>

              <CardActions sx={{ p: 0, pt: 2 }}>
                <Button
                  variant="text"
                  fullWidth
                  size="large"
                  className="enroll-button"
                  sx={{
                    borderRadius: 0,
                    py: 2,
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'text.primary',
                    backgroundColor: 'transparent',
                    border: '1px solid',
                    borderColor: 'divider',
                    opacity: 0.7,
                    transform: 'translateY(8px)',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderColor: 'text.primary',
                      opacity: 1,
                    },
                  }}
                >
                  Access Course
                </Button>
              </CardActions>
            </Card>
            </Grid>
          ))}
        </Grid>
      </Box>



      {/* Empty State */}
      {filteredCourses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No courses found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try adjusting your search terms or browse all available courses.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CourseFeed;
